import * as PIXI from "pixi.js";

import Button from "./lib/Button";
import Session from "./Session";
import { Profile } from "./lib/game_snacks/IUser";
import MessagePopup from "./screen/popup/MessagePopup";
import ServiceLocator from "./ServiceLocator";


export default class Utils
{
    // #region Methods //

    private constructor() {}

    public static randomInteger( min: number, max: number ): number
    {
        let rand = min - 0.5 + Math.random() * (max - min + 1);
        return Math.round( rand );
    }

    public static softShuffle<T>(array: T[]): T[] {
        const newArray = [...array]; // Copia para no modificar el original
        for (let i = newArray.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newArray[i], newArray[j]] = [newArray[j], newArray[i]]; // Destructuring swap
        }
        return newArray;
    };

    public static shuffle( array: any ): void
    {
        for ( let i: number = array.length - 1; i > 0; --i )
        {
            const j: number = this.randomInteger( 0, i );
            const temp: any = array[ i ];
            array[ i ] = array[ j ];
            array[ j ] = temp;
        }
    }

    public static shufflePRNG( array: any ): void
    {
        for ( let i: number = array.length - 1; i > 0; --i )
        {
            const j: number = Session.i.prng.randInterval( 0, i );
            const temp: any = array[ i ];
            array[ i ] = array[ j ];
            array[ j ] = temp;
        }
    }

    public static createButton( title: string ): Button
    {
        let button: Button = new Button( { 
            on: "button_on", 
            pressed: "button_pressed" } );
        button.anchor.set( 0.5 );

        const kPadding: number = 10;
        const kButtonH: number = button.height - kPadding;
        const kButtonW: number = button.width - kPadding;
        
        let text: PIXI.Text = new PIXI.Text( title, { fontSize: 42, fill: "white", stroke: 0x000000, strokeThickness: 4, align: "center" } );
        text.name = "title";
        text.anchor.set( 0.5 );
        button.addChild( text );

        const kScaleX: number = kButtonW / text.width;
        const kScaleY: number = kButtonH / text.height;
        if ( kScaleX < 1 || kScaleY < 1 )
        {
            text.scale.set( Math.min( kScaleX, kScaleY ) );
        }
       
        return button;
    }

    public static isCircleIntersection( a: PIXI.Circle, b: PIXI.Circle ): boolean
    {
        return ( a.radius + b.radius ) * ( a.radius + b.radius ) >= ( a.x - b.x ) * ( a.x - b.x ) + ( a.y - b.y ) * ( a.y - b.y );
    }

    public static isCircleInsideCircle( container: PIXI.Circle, other: PIXI.Circle ): boolean
    {
        return ( container.radius - other.radius ) * ( container.radius - other.radius ) >= ( container.x - other.x ) * ( container.x - other.x ) + ( container.y - other.y ) * ( container.y - other.y )
    }

    public static isPointInsideCircle( point: PIXI.Point, circle: PIXI.Circle ): boolean
    {
        return circle.radius * circle.radius >= ( point.x - circle.x ) * ( point.x - circle.x ) + ( point.y - circle.y ) * ( point.y - circle.y );
    }

    public static isCircleRectangleIntersection( circle: PIXI.Circle, rect: PIXI.Rectangle ): boolean
    {
        const kCircleDistanceX = Math.abs( circle.x - rect.x );
        const kCircleDistanceY = Math.abs( circle.y - rect.y );

        if ( kCircleDistanceX > ( rect.width / 2 + circle.radius ) ) { return false; }
        if ( kCircleDistanceY > ( rect.height / 2 + circle.radius ) ) { return false; }

        if ( kCircleDistanceX <= ( rect.width / 2 )) { return true; } 
        if ( kCircleDistanceY <= ( rect.height / 2 ) ) { return true; }

        const kCornerDistanceSq = Math.pow( kCircleDistanceX - rect.width / 2, 2 ) 
            + Math.pow( kCircleDistanceY - rect.height / 2, 2);

        return ( kCornerDistanceSq <= ( circle.radius * circle.radius ) );
    }

    public static isLineSegToCircleIntersection( o: PIXI.Point, r: number, p: PIXI.Point, q: PIXI.Point ): boolean
    {
        const d: PIXI.Point = new PIXI.Point( q.x - p.x, q.y - p.y );
        const f: PIXI.Point = new PIXI.Point( p.x - o.x, q.y - o.y );
        const a: number = d.x * d.x + d.y * d.y;
        const b: number = 2 * ( f.x * d.x + f.y * d.y );
        const c: number = ( f.x * f.x + f.y * f.y ) - r * r;
        let discriminant = b * b - 4 * a * c;

        if ( discriminant < 0 )
        {
            return false;
        }
        else
        {
            discriminant = Math.sqrt( discriminant );
            const t1: number = ( -b - discriminant ) / ( 2 * a );
            const t2: number = ( -b + discriminant ) / ( 2 * a );
        
            if ( t1 >= 0 && t1 <= 1 )
            {
                return true
            }

            if ( t2 >= 0 && t2 <= 1 )
            {
                return true;
            }
        
            return false;
        }
    }

    public static randomBoolean(): boolean
    {
        return Math.floor( Math.random() * 2 ) == 1;
    }
    
    public static showMessagePopup( message: string, isCloseBtn: boolean = true ): MessagePopup
    {
        let messagePopup: MessagePopup = new MessagePopup();
        messagePopup.message = message;
        messagePopup.isCloseBtn = isCloseBtn;
        messagePopup.onEnter();
        messagePopup.onClosed.add( ( popup: MessagePopup ) => { popup.onLeave(); } );
    
        return messagePopup;
    }

    /**
     * Helper functions

    line intercept math by Paul Bourke http://paulbourke.net/geometry/pointlineplane/
    Determine the intersection point of two line segments
    Return FALSE if the lines don't intersect

    Code modified from original to match pixi examples linting rules.
    */
    public static intersect(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number, x4: number, y4: number): { x: number, y: number } {
        // Check if none of the lines are of length 0
        if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
            return null;
        }

        const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

        // Lines are parallel
        if (denominator === 0) {
            return null;
        }

        const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
        const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

        // is the intersection along the segments
        if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
            return null;
        }

        // Return a object with the x and y coordinates of the intersection
        const x = x1 + ua * (x2 - x1);
        const y = y1 + ua * (y2 - y1);

        return { x, y };
    }

    public static truncateTextToWidth( text: PIXI.Text, width: number ): void
    {
        let textStr: string = text.text;
        while ( text.width > width )
        {
            textStr = textStr.slice( 0, textStr.length - 1 );
            text.text = textStr + "...";
        }
    }

    public static fitTextToWidth( text: PIXI.Text, width: number ): void
    {
        while ( text.width > width )
        {
            text.scale.set( text.scale.x - 0.05 );
        }
    }

    public static generateBotNames( botCount: number ): Array<string>
    {
        let result: Array<string> = [];

        const kArrBotName: Array<string> = [
            "SnuggleCzar", "PurrViper", "PingPongPug", "SneezyLad", "Zomboose", 
            "BibbleSnap", "SnubBlaster", "BorkKnight", "Boopz", "LoOpz",
            "grumpyWiz", "FoamRat", "smEllyOnE", "mArZiPan", "grAvyTime",
            "WaffleTamer", "vErTiGo", "Snicko", "Wompkin", "BlurTuna",
            "cHaRzY", "Loopster", "yOoHoO", "JazzWaddle", "ToastyGhost",
            "Noodlor", "SnarkLord", "DerpQueen", "CouchTamer", "TwinkDino",
            "oBiWaNqT", "Blorp", "JibberJaw", "Prankelstein", "TwistFlop",
            "MochaFang", "NuggetHero", "Bloopkins", "DerpyZoid", "PixelHunter",
            "Hopscotch", "Zogmo", "vAmPiGuY", "TangleButt", "TurboDuck",
            "SneakyLad", "LimpNoodle", "SwAgHoRse", "Booglo", "PounceBean",
            "MuffinMug", "PeaNuTBeAsT", "GargleZ", "DoodleMax", "ShiftyPig",
            "DoinkZ", "Chirpster", "YeLLowBoi", "tWiRlZ", "Twankle",
            "FroGSlAyEr", "PogoWaffle", "Gobblor", "Trickster", "SnipSnap",
            "fLoOfBoY", "SleepySlug", "mOnkEyPaW", "ChubbyChomp", "NumbFlake",
            "MeowMix", "Mopzilla", "DribbleFox", "WaggyYak", "NoodleFlame",
            "Squiggleton", "WhompLad", "SlippyChunk", "SnuggleTrap", "BoopFace",
            "wubbaLuv", "ZapBlaster", "Crinkly", "FluffyFang", "Oopsie",
            "Toadette", "fluffstorm", "GizmoHop", "Snarzo", "RustyKnight",
            "tOaStZilla", "GlitchGal", "NyxShade", "CLoWdY", "bOrEkId",
            "SwogglePuff", "Crumbly", "wHaCkIe", "ePiCqUeeN", "RuNninGmAn",
            "MysticPenguin", "RuffSnort", "DizzyYak", "BlipZap",
            "DoomPlop", "BonkHammer", "sCrUnK", "ZappyJazz", "JigglePuffin",
            "GiggleWorm", "BumpyZerk", "Moozle", "WrangleBean", "FlareToad",
            "ClashBoi", "EchoWolf", "HazelFuzz", "FrizzleTop", "Swishcat",
            "ThrashPup", "nApTiMeKiNg", "CringeGolem", "Flipperz", "sPlAtKaT",
            "KoolCobra", "GiddyGoat", "ZestyMoose", "WaffleKing", "Moozled",
            "XenoMelt", "NoodleZilla", "PogoFish", "PuddleGrunt", "Wobblington",
            "CrypticFox", "FLuFfMaSTa", "FrumpKing", "LunaStrike", "sHaDoWbOi",
            "Sprongle", "ChocoMelon", "Dizzyfang", "Grumpo", "BlazeKitten", 
            "ChunkySpin", "VexMarlin", "SirFuzzle", "SplatYak", "TrippySnack",
            "nOObQueen", "LoafRider", "Whiskr", "JumpySoup", "pRaWnBoI",
            "BrambleJet", "WiLDcAt", "MuffinKnight", "BaNaNaBeaST", "Zernova",
            "VortexNova", "dArKFrUiT", "PipCrush", "Groovelle", "rEktOPus",
            "SirCrumbs", "ZipZap", "tWiTChy", "GloopWolf", "ChunkMancer",
            "Grumbog", "RageToast", "ShadowSniper", "SplatBoi", "MiNiMoose",
            "TubaGoblin", "Florp", "Zoomoose", "HiccupHead", "FlibberFloof",
            "TinyGrowl", "yoMaMa", "VexiDoG", "Smushlord", "Froodle",
            "StinkSnout", "KrazyKoala", "Meltie", "Boinkster", "Boinkie",
            "TRexSocks", "WigglyFuzz", "ZapMoth", "ZigZagger", "Loppy",
            "SoggyToast", "YumSnout", "Skidoodle", "RazzClaw", "MeepLord",
            "FizzyDuck" ];

        let arrBotNameIndex: Array<number> = [];
        while ( result.length < botCount )
        {
            const kBotNameIndex: number = Math.floor( Math.random() * kArrBotName.length );
            if ( !arrBotNameIndex.includes( kBotNameIndex ) )
            {
                arrBotNameIndex.push( kBotNameIndex );

                result.push( kArrBotName[ kBotNameIndex ] );
            }
        }
        
        return result;
    }

    public static createTexArray( textureId: string, textureCount: number ): Array<PIXI.Texture>
    {
        let result: Array<PIXI.Texture> = [];
        for ( let i: number = 0; i < textureCount; ++i )
        {
            result.push( ServiceLocator.resources[ textureId + i.toString() ].texture );
        }

        return result;
    }

    // #endregion //
}